import {
  PLAN_CHANGE_TIERS,
  type PlanChangeCustomerType,
  type PlanChangeTier,
} from './planChange.types'

export type TierDisplayNames = Record<PlanChangeTier, string>

export const TIER_DISPLAY_NAMES: TierDisplayNames = {
  entry: 'Entry',
  starter: 'Starter',
  basic: 'Basic',
  lite: 'Lite',
  essential: 'Essential',
  essential_plus: 'Premium',
  premium: 'Essential',
  premium_plus: 'Premium',
  lite_scorp_group: 'Lite',
}

export type PlanSegment = 'sole_prop' | 'scorp_group'

const TIER_SEGMENT: Partial<Record<PlanChangeTier, PlanSegment>> = {
  lite: 'sole_prop',
  essential: 'sole_prop',
  essential_plus: 'sole_prop',
  premium: 'scorp_group',
  premium_plus: 'scorp_group',
  lite_scorp_group: 'scorp_group',
}

const SEGMENT_SUFFIX: Record<PlanSegment, string> = {
  sole_prop: ' (for Sole Proprietors)',
  scorp_group: ' (for S Corps or Groups)',
}

/** A plan sold to the other customer type carries its segment, so two plans never share a name on one screen. */
const labelledFor = (viewer: PlanSegment): TierDisplayNames => {
  const names = { ...TIER_DISPLAY_NAMES }
  ;(Object.keys(names) as PlanChangeTier[]).forEach((tier) => {
    const segment = TIER_SEGMENT[tier]
    if (segment && segment !== viewer) {
      names[tier] = `${names[tier]}${SEGMENT_SUFFIX[segment]}`
    }
  })
  return names
}

const LABELLED_BY_VIEWER: Record<PlanSegment, TierDisplayNames> = {
  sole_prop: labelledFor('sole_prop'),
  scorp_group: labelledFor('scorp_group'),
}

export const tierDisplayNamesFor = (
  viewer: PlanSegment = 'sole_prop'
): TierDisplayNames => LABELLED_BY_VIEWER[viewer]

const PLAN_CHANGE_SEGMENT_SUFFIX: Record<PlanSegment, string> = {
  sole_prop: ' (for Sole Props)',
  scorp_group: ' (for S Corps or Groups)',
}

export const PLAN_CHANGE_AUDIENCE_SUFFIXES: string[] = Object.values(
  PLAN_CHANGE_SEGMENT_SUFFIX
)

const planChangeNamesFor = (viewer: PlanSegment): TierDisplayNames =>
  PLAN_CHANGE_TIERS.reduce<TierDisplayNames>((names, tier) => {
    const segment = TIER_SEGMENT[tier]
    return {
      ...names,
      [tier]:
        segment && segment !== viewer
          ? `${TIER_DISPLAY_NAMES[tier]}${PLAN_CHANGE_SEGMENT_SUFFIX[segment]}`
          : TIER_DISPLAY_NAMES[tier],
    }
  }, TIER_DISPLAY_NAMES)

const PLAN_CHANGE_NAMES: Record<PlanSegment, TierDisplayNames> = {
  sole_prop: planChangeNamesFor('sole_prop'),
  scorp_group: planChangeNamesFor('scorp_group'),
}

/** The plain name, whoever is looking: a legacy plan is a tier of its own, not one of the other customer type's. */
const legacyNameFor = (tier: PlanChangeTier): string | null =>
  TIER_SEGMENT[tier] ? `${TIER_DISPLAY_NAMES[tier]} (Legacy)` : null

export const planChangeDisplayNamesFor = (
  customerType: PlanChangeCustomerType | null,
  legacyTier: PlanChangeTier | null = null
): TierDisplayNames => {
  const names =
    PLAN_CHANGE_NAMES[
      customerType === 'scorp_group' ? 'scorp_group' : 'sole_prop'
    ]
  const legacyName = legacyTier ? legacyNameFor(legacyTier) : null
  return legacyTier && legacyName
    ? { ...names, [legacyTier]: legacyName }
    : names
}

/** Plain names, with the legacy suffix on the held legacy tier: the audience rides on a separate line. */
export const planChangeBaseNamesFor = (
  legacyTier: PlanChangeTier | null = null
): TierDisplayNames => {
  const legacyName = legacyTier ? legacyNameFor(legacyTier) : null
  return legacyTier && legacyName
    ? { ...TIER_DISPLAY_NAMES, [legacyTier]: legacyName }
    : TIER_DISPLAY_NAMES
}

export type AudienceEyebrows = Partial<Record<PlanChangeTier, string>>

const SOLE_PROP_EYEBROW: Record<PlanChangeCustomerType, string> = {
  solo: 'For Sole Props',
  scorp_group: 'For Non-group Sole Props',
}

const SCORP_GROUP_EYEBROW = 'For S Corps and Groups'

/** Who each plan in a view is for, or nothing when the view shows one customer type's plans. */
export const audienceEyebrowsFor = (
  viewTiers: readonly PlanChangeTier[],
  customerType: PlanChangeCustomerType | null
): AudienceEyebrows => {
  const segments = new Set(viewTiers.map((tier) => TIER_SEGMENT[tier]))
  if (!segments.has('sole_prop') || !segments.has('scorp_group')) return {}
  const solePropEyebrow = SOLE_PROP_EYEBROW[customerType ?? 'solo']
  return viewTiers.reduce<AudienceEyebrows>((eyebrows, tier) => {
    const segment = TIER_SEGMENT[tier]
    if (!segment) return eyebrows
    return {
      ...eyebrows,
      [tier]: segment === 'sole_prop' ? solePropEyebrow : SCORP_GROUP_EYEBROW,
    }
  }, {})
}
